import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clearSavedRouteParams } from 'src/components/EnhancedRouter';
import { LeaderboardFiltersState } from 'src/models/leaderboard/_types';
import { Identifier } from 'src/types/utilityTypes';

const FILTER_KEYS = [
  'locationIds',
  'locationTags',
  'gender',
  'ageMin',
  'ageMax',
] as const;

function parseFilters(params: URLSearchParams): LeaderboardFiltersState {
  const locationIdsRaw = params.get('locationIds');
  const locationTagsRaw = params.get('locationTags');
  const gender = params.get('gender');
  const ageMin = params.get('ageMin');
  const ageMax = params.get('ageMax');

  const locationIds: Identifier[] = locationIdsRaw
    ? locationIdsRaw.split(',').filter(Boolean)
    : [];

  const locationTags: string[] = locationTagsRaw
    ? locationTagsRaw.split(',').filter(Boolean)
    : [];

  return {
    locationId: locationIds.length === 1 ? locationIds[0] : null,
    locationIds,
    locationTags,
    gender: gender || null,
    ageMin: ageMin ? Number(ageMin) : null,
    ageMax: ageMax ? Number(ageMax) : null,
  };
}

export function useLeaderboardFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const setFilters = useCallback(
    (newFilters: LeaderboardFiltersState) => {
      clearSavedRouteParams(window.location.pathname);

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          for (const key of FILTER_KEYS) {
            next.delete(key);
          }

          if (newFilters.locationIds?.length) {
            next.set('locationIds', newFilters.locationIds.join(','));
          }
          if (newFilters.locationTags?.length) {
            next.set('locationTags', newFilters.locationTags.join(','));
          }
          if (newFilters.gender) {
            next.set('gender', newFilters.gender);
          }
          if (newFilters.ageMin != null && newFilters.ageMin > 1) {
            next.set('ageMin', String(newFilters.ageMin));
          }
          if (newFilters.ageMax != null && newFilters.ageMax < 100) {
            next.set('ageMax', String(newFilters.ageMax));
          }

          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return { filters, setFilters };
}
